import { HudDrawer, useHudDrawerSideLabel } from '@sorrem-client/ui/components';
import type { CommissionGoalStatus, CrucibleCommissionState, HudEdge } from '@sorrem/shared';
import { isCommissionComplete, isCommissionGoalVisible } from '@sorrem/shared';
import { HudPanel, Icon } from '@sorrem-client/ui/components';
import type { IconName } from '@sorrem-client/ui/components';
import classnames from 'classnames';
import styles from './CommissionPanel.module.scss';

interface CommissionPanelProps {
    /** The run's commission and the local player's progress, as the Crucible reports them. */
    commission: CrucibleCommissionState;
    open: boolean;
    title?: string;
    onOpenChange: (open: boolean) => void;
    /** The screen edge it slides off: the side of its HUD area (default right, #210). */
    edge?: HudEdge;
}

const STATUS_ICONS: Record<CommissionGoalStatus, IconName> = {
    locked: 'lock',
    active: 'flag',
    complete: 'accept',
};

const STATUS_LABELS: Record<CommissionGoalStatus, string> = {
    locked: 'Locked',
    active: 'In progress',
    complete: 'Complete',
};

/**
 * In-run progress toward the commission this Crucible was provisioned for.
 * Slides off its side's edge, leaving a side label to bring it back.
 */
export function CommissionPanel({ commission, open, title = 'Commission', onOpenChange, edge = 'right' }: CommissionPanelProps): JSX.Element {
    const sideLabel = useHudDrawerSideLabel(edge, title, 'toggle-commission');
    const { goals, progress } = commission;
    const progressByGoalId = new Map(progress.map((entry) => [entry.goalId, entry]));
    const requiredGoals = goals.filter((goal) => goal.isOptional !== true);
    const completedCount = requiredGoals.filter((goal) => progressByGoalId.get(goal.id)?.status === 'complete').length;
    const isComplete = isCommissionComplete(goals, progress);

    return (
        <HudDrawer edge={edge} open={open}>
            <HudPanel
                title={title}
                className={styles.panel}
                bodyClassName={styles.body}
                footerClassName={styles.footer}
                {...sideLabel}
                onSideLabelClick={() => onOpenChange(!open)}
                footer={(
                    <>
                        <span>{`Goals ${completedCount} / ${requiredGoals.length}`}</span>
                        <span className={isComplete ? styles.met : undefined}>{isComplete ? 'Complete' : 'In progress'}</span>
                    </>
                )}
            >
                <div className={styles.title}>{commission.title}</div>
                <div className={styles.issuer}>{commission.issuer}</div>
                <ul className={styles.goals}>
                    {goals.filter((goal) => isCommissionGoalVisible(goal, progressByGoalId.get(goal.id))).map((goal) => {
                        const goalProgress = progressByGoalId.get(goal.id);
                        const status = goalProgress?.status ?? 'locked';
                        return (
                            <li key={goal.id} className={classnames(styles.goal, styles[status])}>
                                <div className={styles.goalHeading}>
                                    <Icon icon={STATUS_ICONS[status]} tooltip={STATUS_LABELS[status]} />
                                    <span>{goal.description}</span>
                                    {goalProgress?.target !== undefined && status !== 'locked' && (
                                        <span className={styles.count}>
                                            {`(${Math.min(goalProgress.current ?? 0, goalProgress.target)} of ${goalProgress.target})`}
                                        </span>
                                    )}
                                    {goal.isOptional === true && <span className={styles.optional}>Optional</span>}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </HudPanel>
        </HudDrawer>
    );
}
